import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enrollmentApi } from '@/api/enrollmentApi';
import type {
  CreateEnrollmentTokenRequest,
  EnrollmentTokenListRequest,
} from '@/types/enrollment';

const KEYS = {
  all: ['enrollment-tokens'] as const,
  list: (f: EnrollmentTokenListRequest) => [...KEYS.all, 'list', f] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
};

export function useEnrollmentTokens(filters: EnrollmentTokenListRequest = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => enrollmentApi.list(filters),
  });
}

export function useEnrollmentToken(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => enrollmentApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateEnrollmentToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateEnrollmentTokenRequest) => enrollmentApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useRevokeEnrollmentToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => enrollmentApi.revoke(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
